/**
 * NotificationsCarousel - Swipeable carousel for unread notifications
 * Shows when user opens app and has unread notifications
 */

import { useState, useRef, useCallback, useEffect } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  Dimensions,
  StyleSheet,
  Animated,
  StatusBar,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { Ionicons } from "@expo/vector-icons";
import { InAppNotification, NotificationTheme } from "../types";
import { getLottieAnimation } from "../utils/lottieAnimations";
import { getThemeById, NOTIFICATION_THEMES } from "../utils/themes";

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");

interface NotificationsCarouselProps {
  visible: boolean;
  notifications: InAppNotification[];
  onDismiss: () => void;
  onMarkAllAsRead: () => void;
  onMarkAsRead: (notificationId: string) => void;
}

/**
 * Single notification slide
 */
const NotificationSlide = ({
  notification,
  isActive,
}: {
  notification: InAppNotification;
  isActive: boolean;
}) => {
  const lottieRef = useRef<LottieView>(null);
  
  // Get theme_id from action_data (where createScoreNotifications stores it)
  const getThemeId = (): string | null => {
    const actionData = notification.action_data as Record<string, unknown> | null;
    if (actionData?.theme_id && typeof actionData.theme_id === "string") {
      return actionData.theme_id;
    }
    return null;
  };
  
  const theme = getThemeById(getThemeId());

  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        lottieRef.current?.play();
      }, 100);
    }
  }, [isActive]);

  /**
   * Get gradient colors from theme
   */
  const getGradientColors = (): [string, string, ...string[]] => {
    if (theme.gradient_middle) {
      return [theme.gradient_start, theme.gradient_middle, theme.gradient_end];
    }
    return [theme.gradient_start, theme.gradient_end];
  };

  /**
   * Get points from notification
   */
  const getPoints = (): number | null => {
    if (!notification?.action_data) return null;
    const actionData = notification.action_data as Record<string, unknown>;
    if (typeof actionData.points === "number") {
      return actionData.points;
    }
    return null;
  };

  const points = getPoints();

  return (
    <View style={styles.slide}>
      <LinearGradient
        colors={getGradientColors()}
        style={styles.slideGradient}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
      >
        {/* Corner decorations */}
        <View style={[styles.cornerCircle, styles.topLeftCircle, { backgroundColor: theme.corner_color_1 + "40" }]} />
        <View style={[styles.cornerCircle, styles.topRightCircle, { backgroundColor: theme.corner_color_2 + "40" }]} />
        <View style={[styles.cornerCircle, styles.bottomLeftCircle, { backgroundColor: theme.corner_color_2 + "40" }]} />
        <View style={[styles.cornerCircle, styles.bottomRightCircle, { backgroundColor: theme.corner_color_1 + "40" }]} />

        {/* Content */}
        <View style={styles.slideContent}>
          {/* Lottie Animation */}
          <View style={styles.lottieContainer}>
            <LottieView
              ref={lottieRef}
              source={getLottieAnimation(notification.lottie_animation)}
              style={styles.lottie}
              loop
              autoPlay={false}
            />
          </View>

          {/* Title */}
          <Text style={styles.title}>{notification.title}</Text>

          {/* Points Badge if applicable */}
          {points !== null && (
            <View style={styles.pointsBadgeContainer}>
              <View style={[styles.pointsBadge, points < 0 && styles.pointsBadgeNegative]}>
                <Text style={[styles.pointsText, points < 0 && styles.pointsTextNegative]}>
                  {points < 0 ? "" : "+"}{points}
                </Text>
                <Text style={styles.pointsLabel}>punten</Text>
              </View>
            </View>
          )}

          {/* Body */}
          <Text style={styles.body}>{notification.body}</Text>
        </View>
      </LinearGradient>
    </View>
  );
};

/**
 * Notifications Carousel Component
 */
export const NotificationsCarousel = ({
  visible,
  notifications,
  onDismiss,
  onMarkAllAsRead,
  onMarkAsRead,
}: NotificationsCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const flatListRef = useRef<FlatList>(null);

  /**
   * Animate in
   */
  useEffect(() => {
    if (visible && notifications.length > 0) {
      setCurrentIndex(0);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    } else {
      fadeAnim.setValue(0);
    }
  }, [visible, fadeAnim, notifications.length]);

  /**
   * Handle scroll end to update current index
   */
  const handleScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const index = Math.round(event.nativeEvent.contentOffset.x / SCREEN_WIDTH);
      setCurrentIndex(index);
      
      // Mark previous notification as read when scrolling away
      if (index > 0) {
        const prevNotification = notifications[index - 1];
        if (prevNotification) {
          onMarkAsRead(prevNotification.id);
        }
      }
    },
    [notifications, onMarkAsRead]
  );

  /**
   * Handle close - mark all as read and dismiss
   */
  const handleClose = useCallback(() => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      onMarkAllAsRead();
      onDismiss();
    });
  }, [fadeAnim, onMarkAllAsRead, onDismiss]);

  /**
   * Handle skip/next
   */
  const handleNext = useCallback(() => {
    if (currentIndex < notifications.length - 1) {
      flatListRef.current?.scrollToIndex({
        index: currentIndex + 1,
        animated: true,
      });
      // Mark current as read
      onMarkAsRead(notifications[currentIndex].id);
    } else {
      // Last one - close
      handleClose();
    }
  }, [currentIndex, notifications, handleClose, onMarkAsRead]);

  if (!visible || notifications.length === 0) return null;

  const isLastSlide = currentIndex === notifications.length - 1;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      statusBarTranslucent
      onRequestClose={handleClose}
    >
      <StatusBar backgroundColor="transparent" barStyle="light-content" />
      <Animated.View style={[styles.container, { opacity: fadeAnim }]}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Text style={styles.headerTitle}>
              {notifications.length > 1 
                ? `${notifications.length} notificaties` 
                : "1 notificatie"}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            accessibilityLabel="Alles sluiten"
            accessibilityRole="button"
          >
            <Ionicons name="close" size={24} color="white" />
          </TouchableOpacity>
        </View>

        {/* Carousel */}
        <FlatList
          ref={flatListRef}
          data={notifications}
          renderItem={({ item, index }) => (
            <NotificationSlide
              notification={item}
              isActive={index === currentIndex}
            />
          )}
          keyExtractor={(item) => item.id}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={handleScrollEnd}
          initialScrollIndex={0}
          getItemLayout={(_, index) => ({
            length: SCREEN_WIDTH,
            offset: SCREEN_WIDTH * index,
            index,
          })}
        />

        {/* Footer with dots and next button */}
        <View style={styles.footer}>
          {/* Page dots */}
          {notifications.length > 1 && (
            <View style={styles.dotsContainer}>
              {notifications.map((_, index) => (
                <View
                  key={index}
                  style={[
                    styles.dot,
                    index === currentIndex && styles.dotActive,
                  ]}
                />
              ))}
            </View>
          )}

          {/* Next/Done button */}
          <TouchableOpacity
            style={styles.nextButton}
            onPress={handleNext}
            accessibilityLabel={isLastSlide ? "Sluiten" : "Volgende"}
            accessibilityRole="button"
          >
            <Text style={styles.nextButtonText}>
              {isLastSlide ? "Sluiten" : "Volgende"}
            </Text>
            <Ionicons
              name={isLastSlide ? "checkmark" : "arrow-forward"}
              size={20}
              color="white"
            />
          </TouchableOpacity>
        </View>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.9)",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 60,
    paddingHorizontal: 20,
    paddingBottom: 16,
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  headerLeft: {
    flex: 1,
  },
  headerTitle: {
    color: "white",
    fontSize: 18,
    fontWeight: "600",
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  slide: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    padding: 20,
    paddingTop: 120,
    paddingBottom: 140,
  },
  slideGradient: {
    flex: 1,
    borderRadius: 32,
    position: "relative",
    overflow: "hidden",
  },
  slideContent: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 32,
  },
  lottieContainer: {
    width: 180,
    height: 180,
    marginBottom: 24,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  title: {
    fontSize: 32,
    fontWeight: "800",
    color: "white",
    textAlign: "center",
    marginBottom: 16,
    textShadowColor: "rgba(0, 0, 0, 0.2)",
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  body: {
    fontSize: 17,
    color: "rgba(255, 255, 255, 0.9)",
    textAlign: "center",
    lineHeight: 24,
    marginTop: 12,
  },
  pointsBadgeContainer: {
    marginVertical: 8,
  },
  pointsBadge: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 20,
    paddingVertical: 12,
    paddingHorizontal: 32,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  pointsBadgeNegative: {
    backgroundColor: "rgba(239, 68, 68, 0.95)",
  },
  pointsText: {
    fontSize: 40,
    fontWeight: "900",
    color: "#7c3aed",
  },
  pointsTextNegative: {
    color: "white",
  },
  pointsLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#6b7280",
    marginTop: -2,
  },
  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingBottom: 50,
    paddingHorizontal: 20,
    alignItems: "center",
  },
  dotsContainer: {
    flexDirection: "row",
    marginBottom: 20,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    marginHorizontal: 4,
  },
  dotActive: {
    backgroundColor: "white",
    width: 24,
  },
  nextButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 24,
    gap: 8,
  },
  nextButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  // Corner circles
  cornerCircle: {
    position: "absolute",
    width: 150,
    height: 150,
    borderRadius: 75,
  },
  topLeftCircle: {
    top: -50,
    left: -50,
  },
  topRightCircle: {
    top: -30,
    right: -60,
  },
  bottomLeftCircle: {
    bottom: -60,
    left: -50,
  },
  bottomRightCircle: {
    bottom: -50,
    right: -60,
  },
});

export default NotificationsCarousel;


